import React, { useState, useEffect } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Check, 
  AlertCircle, 
  Loader2, 
  X,
  FileText,
  Sparkles,
  CheckCircle,
  Clock,
  ArrowRight,
  Brain,
  Database as DatabaseIcon
} from 'lucide-react';
import { frictionlessAsyncApi, ExtractionStatusResponse } from '../services/api/frictionlessAsyncApi';
import { ExtractMultipleAsyncRequest } from '../services/api/frictionlessAsyncApi';
import { getApiUrl } from '@/config/api';
import Logo from '@/components/Logo';
import { Button } from "@/components/ui/button";
import Cookies from 'js-cookie';

interface ProcessingStep {
  id: string;
  title: string;
  description: string;
  icon: React.ElementType;
  status?: 'pending' | 'processing' | 'completed';
}

const RestaurantMenuProcessingAsync: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [currentStep, setCurrentStep] = useState(0);
  const [processingComplete, setProcessingComplete] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [extractionStatus, setExtractionStatus] = useState<ExtractionStatusResponse | null>(null);
  const [jobId, setJobId] = useState<string | null>(null);
  const [isCancelling, setIsCancelling] = useState(false);
  const [elapsedTime, setElapsedTime] = useState(0);
  const [pollIntervalRef, setPollIntervalRef] = useState<NodeJS.Timeout | null>(null);
  const [processingId, setProcessingId] = useState<string | null>(null);

  // Beautiful processing steps with icons
  const steps: ProcessingStep[] = [
    {
      id: 'starting',
      title: 'Preparing Your Menu',
      description: 'Initializing AI extraction pipeline',
      icon: FileText,
      status: 'pending'
    },
    {
      id: 'downloading',
      title: 'Retrieving Menu Data',
      description: 'Securely downloading your menu files',
      icon: DatabaseIcon,
      status: 'pending'
    },
    {
      id: 'converting',
      title: 'Processing Documents',
      description: 'Converting PDFs for AI analysis',
      icon: Sparkles,
      status: 'pending'
    },
    {
      id: 'extracting',
      title: 'AI Extraction',
      description: 'Understanding your unique offerings',
      icon: Brain,
      status: 'pending'
    },
    {
      id: 'categorizing',
      title: 'Organizing Dishes',
      description: 'Structuring menu for optimal presentation',
      icon: Sparkles,
      status: 'pending'
    },
    {
      id: 'validating',
      title: 'Finalizing Results',
      description: 'Verifying accuracy and completeness',
      icon: CheckCircle,
      status: 'pending'
    }
  ];

  // Map backend status to step index
  const getStepIndex = (status: string): number => {
    const statusMap: Record<string, number> = {
      'queued': 0,
      'starting': 0,
      'downloading': 1,
      'converting': 2,
      'extracting': 3,
      'categorizing': 4,
      'validating': 5,
      'completed': 6,
      'failed': -1
    };
    return statusMap[status] ?? 0;
  };

  // Timer effect for elapsed time
  useEffect(() => {
    if (!processingComplete && !error) {
      const timer = setInterval(() => {
        setElapsedTime(prev => prev + 1);
      }, 1000);

      return () => clearInterval(timer);
    }
  }, [processingComplete, error]);

  // Format time helper
  const formatTime = (seconds: number) => {
    const mins = Math.floor(seconds / 60);
    const secs = seconds % 60;
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  };

  useEffect(() => {
    // Check if extraction was already completed (prevents re-running on back navigation)
    const completedJobId = sessionStorage.getItem('extraction_job_id');
    if (sessionStorage.getItem('extraction_completed') === 'true' && completedJobId) {
      // Restore completed state
      const itemsExtracted = sessionStorage.getItem('preview_items_extracted');
      const restaurantName = sessionStorage.getItem('preview_restaurant_name');
      if (itemsExtracted && restaurantName) {
        setProcessingComplete(true);
        setCurrentStep(steps.length);
        setJobId(completedJobId);
        return; // Don't re-run extraction
      }
    }

    // Check source type
    const source = location.state?.source;
    const isPreviewMode = location.state?.preview_mode || location.state?.is_frictionless;
    
    if (source === 'manual_upload') {
      // Manual upload flow - extract from uploaded file
      const sessionId = location.state?.session_id;
      const fileInfo = location.state?.file_info;
      const restaurantInfo = location.state?.restaurant_info;
      
      if (!sessionId || !fileInfo) {
        setError('Upload information missing. Please try uploading again.');
        return;
      }
      
      console.log('🚀 Starting manual upload extraction:', { sessionId, fileInfo, restaurantInfo });
      
      // Clear completion flag before starting new extraction
      sessionStorage.removeItem('extraction_completed');
      sessionStorage.removeItem('extraction_job_id');
      
      // Call the extract-uploaded-menu endpoint with restaurant info
      handleManualUploadExtraction(sessionId, fileInfo, restaurantInfo);
      
    } else if (source === 'frictionless' && isPreviewMode) {
      // Get menu data from sessionStorage (set by MenuSearchResults)
      const storedMenuData = sessionStorage.getItem('pending_menu_extraction');
      if (!storedMenuData) {
        setError('No menu data found. Please go back and try again.');
        return;
      }
      
      const menuData = JSON.parse(storedMenuData) as ExtractMultipleAsyncRequest;
      console.log('🚀 Starting frictionless async extraction:', menuData);
      
      // Clear completion flag before starting new extraction
      sessionStorage.removeItem('extraction_completed');
      sessionStorage.removeItem('extraction_job_id');
      
      // Start async extraction
      startAsyncExtraction(menuData);
    } else if (source === 'authenticated_upload') {
      // Authenticated user upload flow - poll for status
      const procId = location.state?.processingId;
      
      if (!procId) {
        setError('Processing ID missing. Please try uploading again.');
        return;
      }
      
      console.log('🚀 Starting authenticated menu processing:', { processingId: procId });
      
      // Store processingId in state for button to use
      setProcessingId(procId);
      
      // Start polling for status
      startAuthenticatedPolling(procId);
    } else {
      // Get menu data from location state (regular flow)
      const menuData = location.state?.menuData as ExtractMultipleAsyncRequest;
      
      if (!menuData) {
        setError('No menu data found. Please go back and try again.');
        return;
      }

      // Start async extraction
      startAsyncExtraction(menuData);
    }

    // Cleanup on unmount
    return () => {
      frictionlessAsyncApi.stopAllPolling();
      if (pollIntervalRef) {
        clearInterval(pollIntervalRef);
      }
    };
  }, []);

  const handleManualUploadExtraction = async (sessionId: string, fileInfo: any, restaurantInfo?: any) => {
    try {
      setCurrentStep(1); // Start with "Processing Documents"
      
      // Store job ID for cancellation (using sessionId for manual uploads)
      setJobId(sessionId);
      sessionStorage.setItem('extraction_job_id', sessionId);
      
      // Call the extract-uploaded-menu endpoint
      const response = await fetch(getApiUrl('frictionless-onboarding/extract-uploaded-menu'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          session_id: sessionId,
          file_info: fileInfo,
          extract_type: 'manual_upload',
          restaurant_info: restaurantInfo
        })
      });

      if (!response.ok) {
        const errorData = await response.json();
        
        // Check if this is a rate limit error (429) or duplicate extraction (409)
        if (response.status === 429 || response.status === 409) {
          // Navigate to dedicated conversion page
          navigate('/menu-extraction-limit', {
            state: {
              itemsExtracted: 46, // You can parse from error message if needed
              fromUpload: true
            }
          });
          return; // Don't throw, handle gracefully
        }
        
        throw new Error(errorData.detail || 'Failed to extract menu');
      }

      const result = await response.json();
      
      // Check if this is a cached/duplicate menu
      if (result.cached) {
        console.log('📦 Cached menu detected, redirecting to conversion page...');
        
        // Clear any old extraction data to prevent claim UI from showing
        sessionStorage.removeItem('extraction_completed');
        sessionStorage.removeItem('preview_session_token');
        sessionStorage.removeItem('preview_items_extracted');
        sessionStorage.removeItem('preview_restaurant_name');
        sessionStorage.removeItem('extraction_job_id');
        
        // Store minimal data for conversion page display only
        sessionStorage.setItem('cached_menu_items', String(result.total_items));
        sessionStorage.setItem('cached_extraction', 'true');
        
        // Navigate to conversion page instead of showing claim UI
        navigate('/menu-extraction-limit', {
          state: {
            itemsExtracted: result.total_items,
            fromUpload: true,
            cached: true,
            message: "Great news! We've already processed this menu for you."
          }
        });
        return;
      }
      
      // Simulate progress steps for better UX
      setCurrentStep(2); // AI Extraction
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setCurrentStep(3); // Organizing
      await new Promise(resolve => setTimeout(resolve, 1000));
      
      setCurrentStep(4); // Validating
      await new Promise(resolve => setTimeout(resolve, 500));
      
      // Create a status object similar to async extraction
      const status: ExtractionStatusResponse = {
        job_id: sessionId,
        status: 'completed',
        progress: 100,
        current_step: 'Extraction complete',
        processing_complete: true,
        success: true,
        session_token: sessionId,
        restaurant_name: 'Your Restaurant',
        total_items_extracted: result.total_items || 0,
        successful_menus: [{
          menu_type: 'main',
          items_extracted: result.total_items || 0,
          processing_time: result.processing_time || 0
        }],
        failed_menus: []
      };
      
      handleExtractionSuccess(status);
      
    } catch (error: any) {
      console.error('Manual upload extraction error:', error);
      setError(error.message || 'Failed to extract menu. Please try again.');
      setCurrentStep(-1);
    }
  };

  const startAsyncExtraction = async (menuData: ExtractMultipleAsyncRequest) => {
    try {
      console.log('🚀 Starting async extraction:', menuData);

      // Start extraction and get job ID
      const startResponse = await frictionlessAsyncApi.startExtraction(menuData);
      const extractionJobId = startResponse.job_id;
      
      // Store job ID for cancellation
      setJobId(extractionJobId);
      sessionStorage.setItem('extraction_job_id', extractionJobId);
      
      console.log('📋 Extraction job started with ID:', extractionJobId);

      // Poll for status updates
      await frictionlessAsyncApi.pollExtractionStatus(
        extractionJobId,
        (status) => {
          // Update UI with progress
          setExtractionStatus(status);
          setCurrentStep(getStepIndex(status.status));
          
          // Log progress
          console.log(`📊 Progress: ${status.progress}% - ${status.current_step}`);
          
          // Check for completion
          if (status.processing_complete) {
            if (status.success) {
              handleExtractionSuccess(status);
            } else {
              handleExtractionFailure(status);
            }
          }
        }
      );
      
    } catch (error: any) {
      console.error('❌ Extraction error:', error);
      setError(error.message || 'Failed to process menus. Please try again.');
    }
  };

  const handleExtractionSuccess = (status: ExtractionStatusResponse) => {
    // Store session data for post-signup claim
    if (status.session_token) {
      sessionStorage.setItem('preview_session_token', status.session_token);
      sessionStorage.setItem('preview_restaurant_name', status.restaurant_name);
      sessionStorage.setItem('preview_items_extracted', String(status.total_items_extracted || 0));
      sessionStorage.setItem('preview_job_id', status.job_id);
    }

    setProcessingComplete(true);
    setCurrentStep(steps.length); // All steps complete
    
    // Mark extraction as completed to prevent re-running
    sessionStorage.setItem('extraction_completed', 'true');
    sessionStorage.setItem('extraction_job_id', status.job_id);

    // Don't auto-redirect - show results and let user click "Sign Up to Continue"
  };

  const handleExtractionFailure = (status: ExtractionStatusResponse) => {
    const errorMessage = status.errors?.join(', ') || 
                        `Failed to process ${status.failed_menus.length} menu(s)`;
    setError(errorMessage);
    setCurrentStep(-1); // Indicate failure
  };

  const handleCancel = async () => {
    if (!jobId || isCancelling) return;

    setIsCancelling(true);
    try {
      await frictionlessAsyncApi.cancelExtraction(jobId);
      frictionlessAsyncApi.stopAllPolling();
      navigate(-1); // Go back
    } catch (error) {
      console.error('Failed to cancel:', error);
      setIsCancelling(false);
    }
  };

  const startAuthenticatedPolling = async (processingId: string) => {
    // Set initial state
    setCurrentStep(0);
    
    // Get auth token
    const token = Cookies.get('auth_token');
    if (!token) {
      setError('Authentication required. Please log in again.');
      navigate('/restaurant-login');
      return;
    }

    // Poll for status every 2 seconds
    const pollInterval = setInterval(async () => {
      try {
        const response = await fetch(getApiUrl(`menus/${processingId}/status`), {
          headers: {
            'Authorization': `Bearer ${token}`
          }
        });

        if (!response.ok) {
          throw new Error('Failed to get processing status');
        }

        const status = await response.json();
        
        // Update extraction status with current progress
        setExtractionStatus(prev => ({
          ...prev,
          progress: status.progress || 0,
          current_step: status.current_step || 'Processing menu...',
          items_extracted: status.total_items_inserted || 0
        }));
        
        // Update progress
        const progress = status.progress || 0;
        setCurrentStep(Math.floor((progress / 100) * steps.length));
        
        // Check if completed
        if (status.status === 'completed') {
          clearInterval(pollInterval);
          
          // Update extraction status with item count (backend sends total_items_inserted)
          setExtractionStatus(prev => ({
            ...prev,
            total_items_extracted: status.total_items_inserted || 0,
            total_items_inserted: status.total_items_inserted || 0,  // Keep both for compatibility
            progress: 100,
            current_step: 'Extraction complete!'
          }));
          
          setProcessingComplete(true);
          setCurrentStep(steps.length);
          
          // Don't auto-redirect - let user click the Continue button
        } else if (status.status === 'failed') {
          clearInterval(pollInterval);
          setError(status.error_message || 'Menu processing failed. Please try again.');
          setCurrentStep(-1);
        }
      } catch (error) {
        console.error('Polling error:', error);
        clearInterval(pollInterval);
        setError('Failed to check processing status. Please refresh the page.');
      }
    }, 2000); // Poll every 2 seconds

    // Store interval ID for cleanup
    setJobId(processingId);
    setPollIntervalRef(pollInterval);
  };

  const handleTryAgain = () => {
    navigate(-1); // Go back to search
  };

  // Fun facts to display during processing
  const funFacts = [
    "Did you know? Restaurants using Dine AI see 15-30% increase in order value!",
    "Our AI analyzes over 100 attributes per dish to optimize recommendations.",
    "Personalized menus can reduce decision time by 40% for customers.",
    "AI-powered upselling suggestions increase dessert sales by 25%.",
    "Dine AI helps identify your most profitable dishes automatically.",
    "Smart menu optimization can boost revenue by up to 20%.",
    "Our AI learns from millions of dining preferences worldwide."
  ];

  const currentFact = funFacts[Math.floor((elapsedTime / 10) % funFacts.length)];

  return (
    <div className="min-h-screen bg-dineai-blue relative overflow-hidden">
      {/* Beautiful animated background */}
      <div className="absolute inset-0 pointer-events-none">
        <div className="absolute top-20 right-10 w-64 h-64 rounded-full bg-dineai-yellow/20 blur-3xl"></div>
        <div className="absolute bottom-20 left-10 w-80 h-80 rounded-full bg-white/10 blur-3xl"></div>
      </div>

      {/* Progress bar at top */}
      <div className="absolute top-0 left-0 right-0 h-1 bg-white/10">
        <motion.div 
          className="h-full bg-dineai-yellow"
          initial={{ width: '0%' }}
          animate={{ width: processingComplete ? '100%' : `${extractionStatus?.progress || 0}%` }}
          transition={{ duration: 0.5 }}
        />
      </div>

      <div className="container mx-auto px-4 flex items-center justify-center min-h-screen relative z-10 py-12">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5 }}
          className="w-full max-w-3xl"
        >
          {/* Logo */}
          <div className="fixed top-8 left-8 z-50">
            <Logo className="text-white h-8" />
          </div>

          {/* Beautiful Processing Card */}
          <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-8 md:p-10 border border-white/20 shadow-xl">
            {/* Header */}
            <div className="text-center mb-8">
              <div className="inline-block bg-dineai-yellow/20 px-3 py-1.5 rounded-full mb-4">
                <span className="text-dineai-yellow font-medium text-xs flex items-center gap-1.5">
                  <Sparkles className="w-3 h-3" />
                  AI PROCESSING
                </span>
              </div>
              
              <h1 className="text-2xl md:text-3xl font-bold text-white mb-3">
                {error ? 'Processing Error' : processingComplete ? 'Menu Processing Complete!' : 'Processing Your Menu'}
              </h1>
              
              {!processingComplete && !error && (
                <div className="flex flex-col items-center justify-center gap-2 text-white/80">
                  <div className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    <span className="text-sm">Elapsed time: {formatTime(elapsedTime)}</span>
                  </div>
                  {extractionStatus && (
                    <p className="text-white/70 text-sm">
                      {extractionStatus.current_step}
                    </p>
                  )}
                </div>
              )}
            </div>

            {/* Error Display */}
            {error && (
              <motion.div
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                className="bg-red-500/10 border border-red-500/30 rounded-xl p-6 mb-6"
              >
                <div className="flex items-start gap-4">
                  <AlertCircle className="w-6 h-6 text-red-400 flex-shrink-0 mt-0.5" />
                  <div className="flex-1">
                    <h3 className="font-semibold text-red-400 mb-2">Processing Failed</h3>
                    <p className="text-white/80 text-sm mb-4">{error}</p>
                    <Button
                      onClick={handleTryAgain}
                      className="bg-red-500/20 text-white hover:bg-red-500/30 border border-red-500/30"
                    >
                      Try Again
                    </Button>
                  </div>
                </div>
              </motion.div>
            )}

            {/* Beautiful Processing Steps */}
            {!error && (
              <div className="space-y-4 mb-8">
                {steps.map((step, index) => {
                  const isActive = index === currentStep;
                  const isComplete = index < currentStep || processingComplete;
                  const Icon = step.icon;
                  
                  return (
                    <motion.div
                      key={step.id}
                      initial={{ opacity: 0, x: -20 }}
                      animate={{ opacity: 1, x: 0 }}
                      transition={{ delay: index * 0.1 }}
                      className={`
                        flex items-start gap-4 p-4 rounded-xl transition-all
                        ${isActive ? 'bg-white/10 border border-dineai-yellow/50' : ''}
                        ${isComplete && !isActive ? 'opacity-70' : ''}
                        ${!isActive && !isComplete ? 'opacity-40' : ''}
                      `}
                    >
                      <div className={`
                        flex-shrink-0 w-10 h-10 rounded-full flex items-center justify-center
                        ${isComplete ? 'bg-green-500/20' : ''}
                        ${isActive ? 'bg-dineai-yellow/20' : ''}
                        ${!isActive && !isComplete ? 'bg-white/10' : ''}
                      `}>
                        {isComplete ? (
                          <CheckCircle className="w-5 h-5 text-green-400" />
                        ) : isActive ? (
                          <Loader2 className="w-5 h-5 text-dineai-yellow animate-spin" />
                        ) : (
                          <Icon className="w-5 h-5 text-white/50" />
                        )}
                      </div>
                      
                      <div className="flex-1">
                        <h3 className={`
                          font-semibold mb-1
                          ${isActive ? 'text-white' : 'text-white/80'}
                        `}>
                          {step.title}
                        </h3>
                        <p className={`
                          text-sm
                          ${isActive ? 'text-white/80' : 'text-white/60'}
                        `}>
                          {step.description}
                        </p>
                      </div>
                    </motion.div>
                  );
                })}
              </div>
            )}

            {/* Fun Fact */}
            {!processingComplete && !error && (
              <motion.div 
                className="bg-dineai-yellow/10 rounded-xl p-4 mb-6"
                key={currentFact}
                initial={{ opacity: 0, y: 10 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.5 }}
              >
                <p className="text-white/90 text-sm text-center">
                  💡 {currentFact}
                </p>
              </motion.div>
            )}

            {/* Statistics */}
            {extractionStatus && extractionStatus.items_extracted > 0 && !processingComplete && (
              <div className="grid grid-cols-2 gap-4 mb-6">
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-dineai-yellow">
                    {extractionStatus.items_extracted}
                  </div>
                  <div className="text-sm text-white/70">Items Extracted</div>
                </div>
                <div className="bg-white/10 rounded-lg p-4">
                  <div className="text-2xl font-bold text-green-400">
                    {extractionStatus.completed_menus.length}
                  </div>
                  <div className="text-sm text-white/70">Menus Processed</div>
                </div>
              </div>
            )}

            {/* Cancel Option - Very subtle */}
            {!error && !processingComplete && (
              <div className="text-center mt-8">
                <button
                  onClick={handleCancel}
                  disabled={isCancelling}
                  className="text-white/40 hover:text-white/60 text-xs transition-colors duration-200"
                >
                  {isCancelling ? (
                    <span className="inline-flex items-center gap-1">
                      <Loader2 className="w-3 h-3 animate-spin" />
                      Cancelling...
                    </span>
                  ) : (
                    <span className="hover:underline">Cancel</span>
                  )}
                </button>
              </div>
            )}

            {/* Success Message & Continue Button */}
            {processingComplete && (
              <motion.div
                initial={{ opacity: 0, scale: 0.95 }}
                animate={{ opacity: 1, scale: 1 }}
                transition={{ duration: 0.5 }}
                className="text-center"
              >
                <div className="mb-4">
                  <div className="flex items-center justify-center gap-3 mb-3">
                    <motion.div
                      initial={{ scale: 0 }}
                      animate={{ scale: 1 }}
                      transition={{ type: "spring", duration: 0.5 }}
                      className="w-12 h-12 bg-green-500/20 rounded-full flex items-center justify-center"
                    >
                      <CheckCircle className="w-7 h-7 text-green-400" />
                    </motion.div>
                    <p className="text-white/80 text-lg">
                      We've successfully extracted and analyzed your menu!
                    </p>
                  </div>
                  <p className="text-white/70 text-base">
                    Extracted in <span className="text-dineai-yellow font-bold">{formatTime(elapsedTime)}</span>
                  </p>
                </div>
                
                <Button
                  onClick={() => {
                    // Check if user is authenticated
                    const authToken = Cookies.get('auth_token');
                    const searchParams = new URLSearchParams(window.location.search);
                    const isNewLocation = searchParams.get('new_location') === 'true';

                    // Check if this is a frictionless extraction (has jobId but no processingId)
                    const isFrictionlessExtraction = !processingId && jobId && extractionStatus;

                    if (authToken && isNewLocation && processingId) {
                      // Authenticated user adding a new location - go to preview page
                      navigate(`/restaurant-menu-preview?processing_id=${processingId}&source=location&new_location=true`);
                    } else if (authToken && processingId && !isFrictionlessExtraction) {
                      // Authenticated user with valid processingId - go to preview page
                      navigate(`/restaurant-menu-preview?processing_id=${processingId}&source=frictionless`);
                    } else {
                      // Frictionless flow or no valid processingId - always go to signup
                      // This handles both: no auth token OR frictionless extraction with stale auth token
                      console.log('🚀 Navigating to signup - frictionless flow or no valid processingId', {
                        isFrictionlessExtraction,
                        hasAuthToken: !!authToken,
                        processingId,
                        jobId
                      });
                      navigate('/restaurant-signup', {
                        state: {
                          extractionComplete: true,
                          itemsExtracted: extractionStatus?.total_items_extracted ||
                            parseInt(sessionStorage.getItem('preview_items_extracted') || '0'),
                          restaurantName: extractionStatus?.restaurant_name ||
                            sessionStorage.getItem('preview_restaurant_name'),
                          menusProcessed: extractionStatus?.completed_menus?.length || 1
                        }
                      });
                    }
                  }}
                  className="bg-dineai-yellow text-black hover:bg-dineai-yellow/90 py-3 px-8 text-base font-bold rounded-full shadow-lg transform hover:scale-105 transition-all flex items-center gap-2 mx-auto"
                >
                  {/* Determine button text based on flow type */}
                  {(() => {
                    const authToken = Cookies.get('auth_token');
                    const isFrictionlessExtraction = !processingId && jobId && extractionStatus;

                    // For frictionless flow, always show "Create Your Account"
                    if (isFrictionlessExtraction) {
                      return 'Create Your Account';
                    }

                    // For authenticated flow with valid processingId, show "Review Your Menu"
                    if (authToken && processingId) {
                      return 'Review Your Menu';
                    }

                    // Default to "Create Your Account"
                    return 'Create Your Account';
                  })()}
                  <ArrowRight className="w-5 h-5" />
                </Button>
              </motion.div>
            )}

          </div>

          {/* Help text */}
          {!processingComplete && !error && (
            <p className="text-center text-white/60 text-sm mt-6">
              AI is extracting your menu items • This usually takes 1-5 minutes
            </p>
          )}
        </motion.div>
      </div>
    </div>
  );
};

export default RestaurantMenuProcessingAsync;